/**
 * WebAuthn helpers using @simplewebauthn/browser-style JSON exchange but
 * without extra deps: we hand-encode arrays/buffers to base64url.
 */

function b64urlToUint8(b64url) {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const s = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(s);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function uint8ToB64url(bytes) {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (let i = 0; i < arr.length; i++) s += String.fromCharCode(arr[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function transformRegOptions(opts) {
  const o = { ...opts };
  o.challenge = b64urlToUint8(opts.challenge);
  o.user = { ...opts.user, id: b64urlToUint8(opts.user.id) };
  if (opts.excludeCredentials) {
    o.excludeCredentials = opts.excludeCredentials.map((c) => ({ ...c, id: b64urlToUint8(c.id) }));
  }
  return o;
}

function transformAuthOptions(opts) {
  const o = { ...opts };
  o.challenge = b64urlToUint8(opts.challenge);
  if (opts.allowCredentials) {
    o.allowCredentials = opts.allowCredentials.map((c) => ({ ...c, id: b64urlToUint8(c.id) }));
  }
  return o;
}

function serializeRegCredential(cred) {
  return {
    id: cred.id,
    rawId: uint8ToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: uint8ToB64url(cred.response.clientDataJSON),
      attestationObject: uint8ToB64url(cred.response.attestationObject),
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    authenticatorAttachment: cred.authenticatorAttachment,
  };
}

function serializeAuthCredential(cred) {
  return {
    id: cred.id,
    rawId: uint8ToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: uint8ToB64url(cred.response.clientDataJSON),
      authenticatorData: uint8ToB64url(cred.response.authenticatorData),
      signature: uint8ToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? uint8ToB64url(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    authenticatorAttachment: cred.authenticatorAttachment,
  };
}

export function isWebAuthnSupported() {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

export async function beginRegister(api) {
  const { data } = await api.post("/webauthn/register/begin");
  const opts = transformRegOptions(data);
  const cred = await navigator.credentials.create({ publicKey: opts });
  const payload = { challenge: data.challenge, credential: serializeRegCredential(cred) };
  const res = await api.post("/webauthn/register/complete", payload);
  return res.data;
}

export async function beginAuthenticate(api) {
  const { data } = await api.post("/webauthn/authenticate/begin");
  const opts = transformAuthOptions(data);
  const cred = await navigator.credentials.get({ publicKey: opts });
  const payload = { challenge: data.challenge, credential: serializeAuthCredential(cred) };
  const res = await api.post("/webauthn/authenticate/complete", payload);
  return res.data; // { vault_token, expires_in }
}
